import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { API, useAuth } from "../App";
import { isFleetOnly, FLEET_HOME } from "../config/productConfig";
import { ArrowLeft, Camera, Plane } from "lucide-react";
import { copiaDoFicheiro } from "../lib/copiaDoFicheiro";
import { mensagemDeErro } from "../lib/erroApi";

// Registo de voos do próprio piloto. Fica pendente até o DI validar; só então conta para as horas.
const hojeLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const dataPt = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "—");
const hhmm = (min) => `${Math.floor(min / 60)}:${String(min % 60).padStart(2, "0")}`;
const ESTADO = {
  pendente: { txt: "aguarda validação do DI", cls: "text-sky-300" },
  validado: { txt: "validado", cls: "text-emerald-300" },
  recusado: { txt: "recusado", cls: "text-red-300" },
};
const VAZIO = { flight_date: hojeLocal(), aircraft_registration: "", departure_time: "", landing_time: "",
  counter_start: "", counter_end: "", landings: "", aerodrome: "", notes: "",
  tipo: "instrucao", aluno_id: "" };   // tipo e aluno: só para instrutores

const erro = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return "Não foi possível registar o voo.";
};

// Fotos do conta-horas: as horas e os minutos estão em mostradores separados, na descolagem e na aterragem (opcionais)
const MOMENTOS = [
  ["inicio_horas", "Descolagem · horas"], ["inicio_minutos", "Descolagem · minutos"],
  ["fim_horas", "Aterragem · horas"], ["fim_minutos", "Aterragem · minutos"],
];

// Botão de foto com o campo escondido (no iPhone um campo visível de 1 px fazia zoom e deixava o ecrã preto)
function FotoContador({ rotulo, ficheiro, onEscolher, testId }) {
  const campo = useRef(null);
  return (
    <div className="space-y-1">
      <span className="text-slate-300">{rotulo}</span>
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => campo.current?.click()} className="inline-flex h-9 items-center gap-2 rounded-md border border-slate-600 bg-slate-800 px-3 text-sm text-slate-100 hover:border-sky-400" data-testid={testId}>
          <Camera className="h-4 w-4 text-sky-300" /> {ficheiro ? "Trocar" : "Foto"}
        </button>
        <span className="min-w-0 truncate text-slate-400">{ficheiro ? ficheiro.name : "sem foto"}</span>
      </div>
      <input ref={campo} type="file" accept="image/jpeg,image/png,.jpg,.jpeg,.png" className="hidden" onChange={(e) => onEscolher(e.target.files?.[0] || null)} />
    </div>
  );
}

export default function PilotFlightLogPage() {
  const navigate = useNavigate();
  const { user, school } = useAuth();
  // "Voltar" goes to the reservations page on fleet-only, and to the training dashboard otherwise.
  // O administrador (que também é piloto ou instrutor) volta ao seu painel
  const voltar = () => navigate(["admin", "super_admin"].includes(user?.role) ? "/admin" : isFleetOnly(school) ? FLEET_HOME : "/dashboard");
  const [semFicha, setSemFicha] = useState(false);
  const [aeronaves, setAeronaves] = useState([]);
  const [registos, setRegistos] = useState([]);
  const [form, setForm] = useState(VAZIO);
  const [avisos, setAvisos] = useState(null);       // avisos do servidor, à espera de confirmação
  const [aGravar, setAGravar] = useState(false);
  const [fotos, setFotos] = useState({});           // {momento: cópia do ficheiro em memória}
  const [quem, setQuem] = useState(null);           // {kind: "pilot"|"instructor", alunos}
  const instrutor = quem?.kind === "instructor";

  const escolherFoto = async (momento, novo) => {
    if (!novo) return;
    try {
      const copia = await copiaDoFicheiro(novo);
      setFotos((f) => ({ ...f, [momento]: copia }));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const carregar = useCallback(async () => {
    try {
      const [ac, reg, qm] = await Promise.all([
        axios.get(`${API}/di/pedidos-voo/aeronaves`, { withCredentials: true }),
        axios.get(`${API}/di/pedidos-voo/me`, { withCredentials: true }),
        axios.get(`${API}/di/pedidos-voo/quem`, { withCredentials: true }),
      ]);
      setQuem(qm.data || null);
      setAeronaves(ac.data || []);
      setRegistos(reg.data || []);
      setSemFicha(false);
    } catch (e) {
      setSemFicha(e?.response?.status === 403);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const corpo = (confirmar) => ({
    flight_date: form.flight_date,
    aircraft_registration: form.aircraft_registration,
    departure_time: form.departure_time,
    landing_time: form.landing_time,
    counter_start: form.counter_start.trim() || null,
    counter_end: form.counter_end.trim() || null,
    landings: form.landings === "" ? null : Number(form.landings),
    aerodrome: form.aerodrome.trim() || null,
    notes: form.notes.trim(),
    ...(instrutor ? { tipo: form.tipo, aluno_id: form.tipo === "instrucao" ? form.aluno_id || null : null } : {}),
    confirmar,
  });

  const enviar = async (confirmar) => {
    if (!form.aircraft_registration) { toast.error("Escolha a aeronave."); return; }
    if (!form.departure_time || !form.landing_time) { toast.error("Indique a hora de descolagem e de aterragem."); return; }
    if (instrutor && form.tipo === "instrucao" && !form.aluno_id) { toast.error("Escolha o aluno."); return; }
    setAGravar(true);
    try {
      const { data } = await axios.post(`${API}/di/pedidos-voo/me`, corpo(confirmar), { withCredentials: true });
      // As fotos seguem depois de o voo estar registado; se alguma falhar, o voo fica registado na mesma
      const escolhidas = MOMENTOS.filter(([m]) => fotos[m]);
      let enviadas = 0;
      for (const [momento, rotulo] of escolhidas) {
        const fd = new FormData();
        fd.append("momento", momento);
        fd.append("file", fotos[momento], fotos[momento].name);
        try {
          await axios.post(`${API}/di/pedidos-voo/${data.id}/fotos`, fd, { withCredentials: true });
          enviadas += 1;
        } catch (err) {
          toast.error(`${rotulo}: ${mensagemDeErro(err, "a foto não foi enviada")}`);
        }
      }
      toast.success(escolhidas.length
        ? `Voo registado com ${enviadas} foto(s) do conta-horas. Fica a aguardar a validação do DI.`
        : "Voo registado. Fica a aguardar a validação do DI.");
      setFotos({});
      setForm(VAZIO);
      setAvisos(null);
      carregar();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d && typeof d === "object") {
        setAvisos(d.avisos || []);   // o piloto confirma ou corrige; nada foi gravado
      } else {
        toast.error(erro(e));
      }
    } finally {
      setAGravar(false);
    }
  };

  if (semFicha) {
    return (
      <div className="min-h-screen bg-cockpit-bg p-4">
        <Button variant="ghost" onClick={voltar}><ArrowLeft className="w-4 h-4 mr-1" /> Voltar</Button>
        <p className="text-slate-300 mt-6 max-w-md">A tua conta não está ligada a uma ficha de piloto ou de instrutor desta escola. Pede ao administrador para a ligar.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cockpit-bg p-4 space-y-4" data-testid="registar-voo">
      <Button variant="ghost" onClick={voltar}><ArrowLeft className="w-4 h-4 mr-1" /> Voltar</Button>

      <Card className="instrument-panel max-w-xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2 text-sky-300"><Plane className="w-5 h-5" /> Registar um voo</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-xs">
          <p className="text-slate-400">Indique as horas do voo. O DI valida o registo, e só depois o voo conta para as tuas horas.</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {instrutor && (
              <label className="space-y-1">
                <span className="text-slate-300">Tipo de voo *</span>
                <select value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })} className="w-full h-9 rounded-md bg-slate-800 border border-slate-700 px-2 text-sm text-slate-100" data-testid="voo-tipo">
                  <option value="instrucao">Instrução</option>
                  <option value="viagem">Particular</option>
                </select>
              </label>
            )}
            {instrutor && form.tipo === "instrucao" && (
              <label className="space-y-1">
                <span className="text-slate-300">Aluno *</span>
                <select value={form.aluno_id} onChange={(e) => setForm({ ...form, aluno_id: e.target.value })} className="w-full h-9 rounded-md bg-slate-800 border border-slate-700 px-2 text-sm text-slate-100" data-testid="voo-aluno">
                  <option value="">Escolher…</option>
                  {(quem?.alunos || []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </label>
            )}
            <label className="space-y-1">
              <span className="text-slate-300">Data</span>
              <Input type="date" value={form.flight_date} onChange={(e) => setForm({ ...form, flight_date: e.target.value })} className="bg-slate-800 border-slate-700" data-testid="voo-data" />
            </label>
            <label className="space-y-1">
              <span className="text-slate-300">Aeronave *</span>
              <select value={form.aircraft_registration} onChange={(e) => setForm({ ...form, aircraft_registration: e.target.value })} className="w-full h-9 rounded-md bg-slate-800 border border-slate-700 px-2 text-sm text-slate-100" data-testid="voo-aeronave">
                <option value="">Escolher…</option>
                {aeronaves.map((a) => <option key={a.registration} value={a.registration}>{a.registration} · {a.model}</option>)}
              </select>
            </label>
            <label className="space-y-1">
              <span className="text-slate-300">Hora de descolagem *</span>
              <Input type="time" value={form.departure_time} onChange={(e) => setForm({ ...form, departure_time: e.target.value })} className="bg-slate-800 border-slate-700" data-testid="voo-descolagem" />
            </label>
            <label className="space-y-1">
              <span className="text-slate-300">Hora de aterragem *</span>
              <Input type="time" value={form.landing_time} onChange={(e) => setForm({ ...form, landing_time: e.target.value })} className="bg-slate-800 border-slate-700" data-testid="voo-aterragem" />
            </label>
            <label className="space-y-1">
              <span className="text-slate-300">Conta-horas inicial (opcional)</span>
              <Input value={form.counter_start} onChange={(e) => setForm({ ...form, counter_start: e.target.value })} placeholder="1234:30" className="bg-slate-800 border-slate-700" data-testid="voo-contador-inicial" />
            </label>
            <label className="space-y-1">
              <span className="text-slate-300">Conta-horas final (opcional)</span>
              <Input value={form.counter_end} onChange={(e) => setForm({ ...form, counter_end: e.target.value })} placeholder="1235:30" className="bg-slate-800 border-slate-700" data-testid="voo-contador-final" />
            </label>
            <label className="space-y-1">
              <span className="text-slate-300">Aterragens (opcional)</span>
              <Input type="number" min={0} max={99} value={form.landings} onChange={(e) => setForm({ ...form, landings: e.target.value })} className="bg-slate-800 border-slate-700" data-testid="voo-aterragens" />
            </label>
            <label className="space-y-1">
              <span className="text-slate-300">Aeródromo (código, ex.: LPST)</span>
              <Input value={form.aerodrome} onChange={(e) => setForm({ ...form, aerodrome: e.target.value.toUpperCase() })} maxLength={4} className="bg-slate-800 border-slate-700" data-testid="voo-aerodromo" />
            </label>
          </div>
          <div className="space-y-2" data-testid="voo-fotos">
            <p className="text-slate-300">Fotos do conta-horas (opcional): horas e minutos, na descolagem e na aterragem</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {MOMENTOS.map(([momento, rotulo]) => (
                <FotoContador key={momento} rotulo={rotulo} ficheiro={fotos[momento]} onEscolher={(f) => escolherFoto(momento, f)} testId={`voo-foto-${momento}`} />
              ))}
            </div>
          </div>
          <label className="space-y-1 block">
            <span className="text-slate-300">Notas (opcional)</span>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} maxLength={300} className="bg-slate-800 border-slate-700" data-testid="voo-notas" />
          </label>

          {avisos && (
            <div className="rounded border border-amber-700 bg-amber-950/40 p-3 space-y-2" data-testid="voo-avisos">
              <p className="text-amber-200">Há avisos sobre este registo. Se estiver certo, registe mesmo assim:</p>
              <ul className="list-disc pl-5 text-amber-100 space-y-1">
                {avisos.map((a) => <li key={a.codigo}>{a.texto}</li>)}
              </ul>
              <div className="flex flex-wrap gap-2 justify-end">
                <Button size="sm" variant="outline" onClick={() => setAvisos(null)} data-testid="voo-corrigir">Corrigir</Button>
                <Button size="sm" onClick={() => enviar(true)} disabled={aGravar} data-testid="voo-registar-mesmo-assim">Registar mesmo assim</Button>
              </div>
            </div>
          )}

          {!avisos && (
            <div className="flex justify-end">
              <Button onClick={() => enviar(false)} disabled={aGravar} data-testid="voo-registar">Registar voo</Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="instrument-panel max-w-xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-base text-slate-200">Os meus voos registados</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-xs">
          {registos.length === 0 && <p className="text-slate-400">Ainda não registou voos.</p>}
          {registos.map((r) => (
            <div key={r.id} className="border-t border-slate-800 pt-2 flex flex-wrap items-center justify-between gap-2" data-testid="meu-voo">
              <span className="text-slate-200">
                {dataPt(r.flight_date)} · {r.aircraft_registration} · {r.departure_time}–{r.landing_time} ({hhmm(r.duration_minutes)})
                {r.flight_type && <span className="text-slate-400"> · {r.flight_type === "instrucao" ? `Instrução${r.aluno_name ? ` (${r.aluno_name})` : ""}` : "Particular"}</span>}
                {r.fotos && Object.keys(r.fotos).length > 0 && <span className="text-slate-400"> · {Object.keys(r.fotos).length} foto(s)</span>}
              </span>
              <span className="text-right">
                <span className={ESTADO[r.status]?.cls || "text-slate-400"}>{ESTADO[r.status]?.txt || r.status}</span>
                {r.status === "recusado" && r.nota_recusa && <span className="block text-red-200">Motivo: {r.nota_recusa}</span>}
              </span>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
